import { useEffect, useRef, useState, useCallback } from 'react';

// SignaturePad — clean-room adopt-signature capture. Two modes:
//   • Draw — freehand on a high-DPI canvas (finger / mouse / stylus)
//   • Type — a typed name rasterized in a script face
// Emits a transparent-background PNG data URL via onChange (or null when empty);
// the server trims the margin before embedding, so transparent + untrimmed here
// is fine. Original implementation — no signature_pad dependency.

const SCRIPT_FONT = "'Dancing Script', cursive";

// Ensure the script webfont is present so the typed signature rasterizes in the
// intended face rather than a system fallback.
function ensureScriptFont() {
  const id = 'sw-dancing-script';
  if (!document.getElementById(id)) {
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Dancing+Script:wght@600&display=swap';
    document.head.appendChild(link);
  }
}

export default function SignaturePad({ onChange, penColor = '#000050', height = 180, placeholder = 'Type your full name' }) {
  const [mode, setMode] = useState('draw');
  const [typed, setTyped] = useState('');
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef(null);
  const hasInk = useRef(false);

  useEffect(() => { ensureScriptFont(); }, []);

  // Size the draw canvas to its box at device pixel ratio for crisp strokes.
  const sizeCanvas = useCallback(() => {
    const c = canvasRef.current;
    if (!c) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = c.getBoundingClientRect();
    c.width = Math.round(rect.width * dpr);
    c.height = Math.round(rect.height * dpr);
    const ctx = c.getContext('2d');
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 2.2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = penColor;
  }, [penColor]);

  useEffect(() => {
    if (mode !== 'draw') return;
    sizeCanvas();
    window.addEventListener('resize', sizeCanvas);
    return () => window.removeEventListener('resize', sizeCanvas);
  }, [mode, sizeCanvas]);

  function pos(e) {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  }

  function startDraw(e) {
    drawing.current = true;
    last.current = pos(e);
    e.target.setPointerCapture?.(e.pointerId);
  }
  function moveDraw(e) {
    if (!drawing.current) return;
    const ctx = canvasRef.current.getContext('2d');
    const p = pos(e);
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
    hasInk.current = true;
  }
  function endDraw() {
    if (!drawing.current) return;
    drawing.current = false;
    emit();
  }

  function emit() {
    if (mode === 'draw') {
      onChange?.(hasInk.current ? canvasRef.current.toDataURL('image/png') : null);
    }
  }

  function clear() {
    if (mode === 'draw') {
      const c = canvasRef.current;
      c.getContext('2d').clearRect(0, 0, c.width, c.height);
      hasInk.current = false;
    } else {
      setTyped('');
    }
    onChange?.(null);
  }

  // Rasterize the typed name to a transparent PNG in the script face.
  async function rasterizeTyped(name) {
    const value = name.trim();
    if (!value) { onChange?.(null); return; }
    try { await document.fonts.load(`48px ${SCRIPT_FONT}`); } catch { /* fallback face */ }
    const dpr = window.devicePixelRatio || 1;
    const off = document.createElement('canvas');
    const ctx = off.getContext('2d');
    const fontPx = 48;
    ctx.font = `${fontPx}px ${SCRIPT_FONT}`;
    const w = Math.max(10, Math.ceil(ctx.measureText(value).width) + 24);
    const h = Math.ceil(fontPx * 1.6);
    off.width = Math.round(w * dpr);
    off.height = Math.round(h * dpr);
    const c2 = off.getContext('2d');
    c2.scale(dpr, dpr);
    c2.font = `${fontPx}px ${SCRIPT_FONT}`;
    c2.fillStyle = penColor;
    c2.textBaseline = 'middle';
    c2.fillText(value, 12, h / 2);
    onChange?.(off.toDataURL('image/png'));
  }

  function onTyped(e) {
    setTyped(e.target.value);
    rasterizeTyped(e.target.value);
  }

  function switchMode(m) {
    if (m === mode) return;
    setMode(m);
    onChange?.(null);
    if (m === 'draw') hasInk.current = false;
  }

  const tabBase = 'flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors';
  return (
    <div>
      <div className="flex gap-1 mb-2.5 bg-surface-strong rounded-lg p-1">
        <button type="button" onClick={() => switchMode('draw')}
          className={`${tabBase} ${mode === 'draw' ? 'bg-accent text-white' : 'text-text-muted hover:text-text'}`}>Draw</button>
        <button type="button" onClick={() => switchMode('type')}
          className={`${tabBase} ${mode === 'type' ? 'bg-accent text-white' : 'text-text-muted hover:text-text'}`}>Type</button>
      </div>

      {mode === 'draw' ? (
        <div>
          <div className="border border-dashed border-border rounded-lg bg-white overflow-hidden">
            <canvas
              ref={canvasRef}
              style={{ display: 'block', width: '100%', height: `${height}px`, cursor: 'crosshair', touchAction: 'none' }}
              onPointerDown={startDraw}
              onPointerMove={moveDraw}
              onPointerUp={endDraw}
              onPointerLeave={endDraw}
            />
          </div>
          <div className="flex justify-between items-center mt-2 text-xs text-text-dim">
            <span>Sign with your finger or mouse.</span>
            <button type="button" onClick={clear} className="text-accent font-medium px-1.5 py-1">Clear</button>
          </div>
        </div>
      ) : (
        <div>
          <input
            type="text" value={typed} onChange={onTyped} placeholder={placeholder}
            className="w-full px-3 py-2.5 border border-border rounded-lg text-text bg-white outline-none focus:border-accent"
          />
          {typed.trim() && (
            <div className="mt-2.5 p-3.5 bg-surface rounded-lg border border-border">
              <div className="text-xs text-text-dim mb-1">Signature preview</div>
              <div style={{ fontFamily: SCRIPT_FONT, fontSize: 32, color: penColor, lineHeight: 1.1, wordBreak: 'break-word' }}>
                {typed}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
